import type { SecurityRead } from 'api';
import type { ClearCellTarget } from 'services/captable';
import type { CitationReferenceData, MappingReferenceData, TokenReference } from 'services/reference-generator/types';
import type { ReviewItem } from 'store/useReviewStore';
import { aiClearFieldsForSecurity } from './aiFieldMap';
import { addManualCitation, type ManualCitationMode } from './manualCitationAdd';
import { citationReviewStatus, isReviewItemActionable, mappingReviewStatus, reviewItemId } from './reviewAggregator';
import { isPendingMeta } from './securityAiPatches';

/** Fields the multi-cell citation planner needs from a grid cell. */
export type CitationTargetCell = {
  alias?: string;
  columnId?: string | number | null;
  key?: string;
  value?: unknown;
  isTitleCell?: boolean;
  isTitleOrHeader?: boolean;
  isTitleWithLabel?: boolean;
  isLegend?: boolean;
  isTotal?: boolean | null;
  expr?: string | null | number;
  citationReferences?: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  aiFieldMetadata?: unknown;
};

export type ManualCitationPayload = {
  documentReferenceId: number;
  tokenReference: TokenReference;
};

export type RejectedCitationTarget = {
  cell: CitationTargetCell;
  reason: string;
};

export type CellCitationPlan = {
  cell: CitationTargetCell;
  citationReferences: CitationReferenceData[];
  mappingReferences?: MappingReferenceData[];
  /** True when applying the plan also removes the cell's AI suggestion. */
  clearsSuggestion?: boolean;
  mutated: boolean;
};

export const sameRefs = <T>(left: T[] | undefined, right: T[]): boolean =>
  (left?.length ?? 0) === right.length && (left ?? []).every((item, index) => item === right[index]);

const planCells = (
  cells: CitationTargetCell[],
  nextRefs: (cell: CitationTargetCell) => CitationReferenceData[]
): CellCitationPlan[] =>
  cells.map(cell => {
    const citationReferences = nextRefs(cell);
    return { cell, citationReferences, mutated: !sameRefs(cell.citationReferences, citationReferences) };
  });

/** Review-item id for a security field cell. */
export const citationCellId = (cell: CitationTargetCell): string | null => {
  if (cell.columnId == null || cell.columnId === '' || !cell.alias) {
    return null;
  }
  return reviewItemId(Number(cell.columnId), cell.alias);
};

/** Why this cell cannot receive a PDF citation, or null when it can. */
export const citationTargetRejection = (cell: CitationTargetCell): string | null => {
  if (cell.isLegend || cell.isTitleCell || cell.isTitleOrHeader || cell.isTitleWithLabel) {
    return 'Table headings and labels cannot hold a citation';
  }
  if (cell.isTotal) {
    return 'Total cells cannot hold a citation';
  }
  if (!cell.alias) {
    return 'Row labels cannot hold a citation';
  }
  if (cell.columnId == null || cell.columnId === '') {
    return 'This cell is not a security field';
  }
  return null;
};

export const classifyCitationTargets = (
  cells: CitationTargetCell[]
): { supported: CitationTargetCell[]; rejected: RejectedCitationTarget[] } => {
  const supported: CitationTargetCell[] = [];
  const rejected: RejectedCitationTarget[] = [];
  cells.forEach(cell => {
    const reason = citationTargetRejection(cell);
    if (reason) {
      rejected.push({ cell, reason });
    } else {
      supported.push(cell);
    }
  });
  return { supported, rejected };
};

export const planApplyCitationToCells = (
  cells: CitationTargetCell[],
  payload: ManualCitationPayload,
  mode: ManualCitationMode = 'append'
): { applied: CellCitationPlan[]; rejected: RejectedCitationTarget[] } => {
  const { supported, rejected } = classifyCitationTargets(cells);
  return {
    applied: planCells(supported, cell =>
      addManualCitation(
        cell.citationReferences,
        {
          fieldName: cell.alias,
          documentReferenceId: payload.documentReferenceId,
          tokenReference: payload.tokenReference,
        },
        mode
      )
    ),
    rejected,
  };
};

/** Clear keeps only the value: every citation, Excel reference and AI suggestion on the cell goes. */
export const planClearCells = (cells: CitationTargetCell[]): CellCitationPlan[] =>
  classifyCitationTargets(cells).supported.map(cell => ({
    cell,
    citationReferences: [],
    mappingReferences: [],
    clearsSuggestion: true,
    mutated: Boolean(cell.citationReferences?.length || cell.mappingReferences?.length || cell.aiFieldMetadata),
  }));

/** The cells a Clear sends to the server; unsaved columns (id 0) have nothing stored there. */
export const clearCellTargets = (plans: CellCitationPlan[], securities: SecurityRead[] = []): ClearCellTarget[] =>
  plans
    .filter(plan => plan.mutated && Number(plan.cell.columnId) > 0)
    .flatMap(({ cell }) =>
      aiClearFieldsForSecurity(
        securities.find(security => security.id === Number(cell.columnId)),
        String(cell.alias)
      ).map(fieldName => ({ security_id: Number(cell.columnId), field_name: fieldName }))
    );

/** Review-panel cell ids for the selected security fields (filter only). */
export const reviewCellIdsForSelection = (cells: CitationTargetCell[]): string[] =>
  classifyCitationTargets(cells)
    .supported.map(citationCellId)
    .filter((id): id is string => id != null);

/** True when this cell still has a pending AI value, PDF citation, or Excel mapping. */
export const cellHasPendingSuggestion = (cell: CitationTargetCell): boolean => {
  if (isPendingMeta(cell.aiFieldMetadata)) {
    return true;
  }
  if ((cell.citationReferences ?? []).some(ref => citationReviewStatus(ref) === 'pending')) {
    return true;
  }
  return mappingReviewStatus(cell.mappingReferences) === 'pending';
};

/** Pending review cards that Accept or Reject settles for the selected cells. */
export const pendingReviewItemsForSelection = (cells: CitationTargetCell[], items: ReviewItem[]): ReviewItem[] => {
  const ids = new Set(reviewCellIdsForSelection(cells));
  return items.filter(item => ids.has(item.id) && item.status === 'pending' && isReviewItemActionable(item));
};
